/**
 * Result page: report, export controls and (for Pro) share links.
 */

import { useState } from 'react';
import type { Entitlements, QuotaStatus } from '@core/types';
import { Alert, Spinner } from '../components/ui';
import { PageHead } from '../components/Layout';
import { ReportView } from '../components/ReportView';
import { api, ApiError, type DiagnosisResponse } from '../lib/api';
import { useAsync, useCopy } from '../lib/hooks';
import { downloadExport, buildExport } from '../lib/export';
import { useRouter, Link } from '../lib/router';
import { useSession } from '../lib/session';

function ShareControls({
  diagnosisId,
  entitlements,
  getToken,
}: {
  diagnosisId: string;
  entitlements: Entitlements;
  getToken: () => Promise<string | null>;
}) {
  const [busy, setBusy] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [created, setCreated] = useState<{ url: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { copied, copy } = useCopy();

  if (!entitlements.shareableReports) {
    return (
      <Alert tone="info" title="Shareable reports are a Pro feature">
        <p>
          Pro accounts can create revocable private links that expose only the sanitized input and
          the generated report. Upgrading also adds JSON export and persistent history.
        </p>
        <Link className="button button--small" to="/app/billing">
          See upgrade options
        </Link>
      </Alert>
    );
  }

  return (
    <div className="stack stack--tight">
      {error ? <Alert tone="error">{error}</Alert> : null}
      {created ? (
        <Alert tone="success" title="Share link created">
          <p className="small">
            Copy this link now. Only a hash of the token is stored, so it cannot be shown again. It
            expires on {new Date(created.expiresAt).toLocaleString()}.
          </p>
          <div className="row">
            <input type="text" readOnly value={created.url} aria-label="Share link" />
            <button
              type="button"
              className="button--small"
              onClick={() => copy(created.url, 'share')}
            >
              {copied === 'share' ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </Alert>
      ) : (
        <div className="row">
          <label htmlFor="share-days" style={{ margin: 0 }}>
            Expires in
          </label>
          <select
            id="share-days"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(Number(event.target.value))}
            style={{ width: 'auto' }}
          >
            {[1, 7, 14, 30, 60, 90].map((days) => (
              <option key={days} value={days}>
                {days} day{days === 1 ? '' : 's'}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="button--small"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const token = await getToken();
                const result = await api.createShare(token, diagnosisId, expiresInDays);
                setCreated({ url: result.share.url, expiresAt: result.share.expiresAt });
              } catch (caught) {
                setError(
                  caught instanceof ApiError
                    ? `${caught.message}${caught.hint ? ` ${caught.hint}` : ''}`
                    : 'The share link could not be created.',
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Creating…' : 'Create share link'}
          </button>
        </div>
      )}
    </div>
  );
}

export interface DiagnosisResultProps {
  id: string;
  entitlements: Entitlements;
  quota: QuotaStatus;
  onQuotaChange: (quota: QuotaStatus) => void;
}

export function DiagnosisResultPage({ id, entitlements, onQuotaChange }: DiagnosisResultProps) {
  const { getToken } = useSession();
  const { navigate } = useRouter();
  const state = useAsync(async (token) => api.getDiagnosis(token, id), [id]);
  const [showSanitized, setShowSanitized] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (state.loading) {
    return (
      <div className="card row">
        <Spinner label="Loading the diagnosis" /> <span>Loading the diagnosis…</span>
      </div>
    );
  }

  if (state.error) {
    return (
      <Alert tone="error" title="The diagnosis could not be loaded">
        <p>{state.error.message}</p>
        <button type="button" className="button--small" onClick={state.reload}>
          Try again
        </button>
      </Alert>
    );
  }

  const data: DiagnosisResponse | null = state.data;
  if (!data) return null;
  const { diagnosis } = data;

  return (
    <>
      <PageHead
        title="Diagnosis result"
        description="Read the summary first, then work through the ranked causes. Every item states how confident it is and why."
        actions={
          <div className="row">
            <Link className="button button--small" to="/app">
              New diagnosis
            </Link>
            <Link className="button button--small" to="/app/diagnoses">
              History
            </Link>
          </div>
        }
      />

      {data.replayed ? (
        <Alert tone="info" title="Existing result returned">
          This idempotency key had already produced a diagnosis, so the stored result was returned
          without consuming another quota unit.
        </Alert>
      ) : null}

      {data.notices.length > 0 ? (
        <Alert tone="info" title="Notes">
          <ul className="plain-list">
            {data.notices.map((notice) => (
              <li key={notice}>{notice}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <ReportView
        report={diagnosis.report}
        sanitizedInput={diagnosis.sanitizedInput}
        createdAt={diagnosis.createdAt}
        expiresAt={diagnosis.expiresAt}
        showSanitizedInput={showSanitized}
        actions={
          <div className="row">
            <button
              type="button"
              className="button--small"
              onClick={() => downloadExport(diagnosis.report, 'markdown')}
            >
              Export Markdown
            </button>
            {entitlements.exportFormats.includes('json') ? (
              <button
                type="button"
                className="button--small"
                onClick={() => downloadExport(diagnosis.report, 'json')}
              >
                Export JSON
              </button>
            ) : (
              <button
                type="button"
                className="button--small"
                title="JSON export is a Pro feature"
                aria-disabled="true"
                onClick={() => navigate('/app/billing')}
              >
                JSON export (Pro)
              </button>
            )}
            <button
              type="button"
              className="button--small"
              onClick={() => setShowSanitized((value) => !value)}
              aria-expanded={showSanitized}
            >
              {showSanitized ? 'Hide sanitized input' : 'Show sanitized input'}
            </button>
          </div>
        }
      />

      <section className="card" aria-labelledby="result-sharing">
        <h3 id="result-sharing">Sharing</h3>
        <ShareControls diagnosisId={diagnosis.id} entitlements={entitlements} getToken={getToken} />
      </section>

      <section className="card">
        <h3>Manage this diagnosis</h3>
        <div className="row">
          <button
            type="button"
            className="button--small"
            onClick={() => {
              void navigator.clipboard?.writeText(buildExport(diagnosis.report, 'markdown'));
            }}
          >
            Copy full report
          </button>
          <button
            type="button"
            className="button--danger button--small"
            disabled={deleting}
            onClick={async () => {
              if (!window.confirm('Delete this diagnosis permanently? This cannot be undone.'))
                return;
              setDeleting(true);
              try {
                const token = await getToken();
                await api.deleteDiagnosis(token, diagnosis.id);
                const refreshed = await api.me(token);
                onQuotaChange(refreshed.quota);
                navigate('/app/diagnoses');
              } catch {
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete diagnosis'}
          </button>
        </div>
      </section>
    </>
  );
}
